import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import mqtt, { MqttClient } from 'mqtt';
import RootState from '../redux/states/root.state';
import {
    fetchV1ClockInfo,
    requestV1Clock,
    setV1Clock,
    V1ClockInfo,
    V1ClockTime,
} from '../services/api/v1Mqtt.api';
import { clockToMs, msToClock, parseTubClock } from '../services/mqtt/v1/v1Clock';

/** How long to wait for the tub's answer before calling it unavailable. */
const REPLY_TIMEOUT_MS = 10000;

export type V1ClockStatus = 'idle' | 'loading' | 'ok' | 'unavailable' | 'error';

/**
 * A V1 / Coldfire tub's clock, as classic's details page reads it: listen on the classic
 * web broker for `UpdateTubClock`, then ask the tub (through the backend, which sends a
 * clock packet to the legacy spa server). The tub answers about a second later, and
 * echoes its new time after a set.
 *
 * `clock` is the tub's time advanced by the time since it answered, so it ticks.
 * Pass an empty `spaId` to keep it idle.
 */
const useV1Clock = (spaId: string) => {
    const accessToken = useSelector((state: RootState) => state.user.accessToken);
    const [info, setInfo] = useState<V1ClockInfo | null>(null);
    const [reading, setReading] = useState<{ time: V1ClockTime; at: number } | null>(null);
    const [status, setStatus] = useState<V1ClockStatus>('idle');
    const [now, setNow] = useState(() => Date.now());
    const clientRef = useRef<MqttClient | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const answeredRef = useRef(false);

    const waitForAnswer = useCallback(() => {
        answeredRef.current = false;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            if (!answeredRef.current) setStatus('unavailable');
        }, REPLY_TIMEOUT_MS);
    }, []);

    const refresh = useCallback(async () => {
        if (!spaId) return;
        setStatus('loading');
        try {
            await requestV1Clock(accessToken, spaId);
            waitForAnswer();
        } catch {
            setStatus('error');
        }
    }, [accessToken, spaId, waitForAnswer]);

    /** Set the tub's clock; resolves once the backend has sent it, the echo updates `clock`. */
    const setClock = useCallback(async (time: V1ClockTime) => {
        await setV1Clock(accessToken, spaId, time);
        setStatus('loading');
        waitForAnswer();
    }, [accessToken, spaId, waitForAnswer]);

    useEffect(() => {
        setInfo(null);
        setReading(null);
        setStatus('idle');
        if (!spaId) return;

        let cancelled = false;
        setStatus('loading');

        (async () => {
            let clockInfo: V1ClockInfo;
            try {
                clockInfo = await fetchV1ClockInfo(accessToken, spaId);
            } catch {
                if (!cancelled) setStatus('error');
                return;
            }
            if (cancelled) return;
            setInfo(clockInfo);

            if (!clockInfo.web_broker_url) {
                setStatus('error');
                return;
            }

            // Classic's own listener: anonymous, the spa GUID as username.
            const client = mqtt.connect(clockInfo.web_broker_url, {
                username: clockInfo.spa_guid.toLowerCase(),
                clientId: `portal-clock-${Math.random().toString(16).slice(2, 10)}`,
                reconnectPeriod: 5000,
                clean: true,
            });
            clientRef.current = client;

            let asked = false;
            client.on('connect', () => {
                client.subscribe(clockInfo.reply_topic, { qos: 0 }, (err) => {
                    if (err || cancelled || asked) return;
                    // Subscribed first, so the answer cannot arrive before we listen.
                    asked = true;
                    refresh();
                });
            });
            client.on('message', (topic, message) => {
                if (topic !== clockInfo.reply_topic) return;
                let payload: unknown;
                try { payload = JSON.parse(message.toString()); } catch { return; }
                const time = parseTubClock(payload, clockInfo);
                if (!time || cancelled) return;
                answeredRef.current = true;
                setReading({ time, at: Date.now() });
                setStatus('ok');
            });
            client.on('error', (err) => console.warn('[v1 clock] web broker error:', err?.message || err));
        })();

        return () => {
            cancelled = true;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            clientRef.current?.end(true);
            clientRef.current = null;
        };
        // refresh is stable for a spa and token; re-running on it would reconnect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, spaId]);

    // Tick while there is a clock to show.
    useEffect(() => {
        if (!reading) return;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [reading]);

    const clock = reading ? msToClock(clockToMs(reading.time) + Math.max(0, now - reading.at)) : null;

    return { info, clock, status, refresh, setClock };
};

export default useV1Clock;
